import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import {
  ApiError,
  apiRequest,
  clearClientSession,
  restoreSession,
  setAccessToken,
  setOutletContext,
} from '../lib/api-client';
import type { AuthSession, AuthUser } from '../lib/api-client';
import { Customers } from './Customers';
import { CustomOrders } from './CustomOrders';
import { FinancialReports } from './FinancialReports';
import { Finance } from './Finance';
import { Inventory } from './Inventory';
import { MasterData } from './MasterData';
import type { ResourceKey } from './MasterData';
import { OperationalDashboard } from './OperationalDashboard';
import { Products } from './Products';
import { Sales } from './Sales';
import { Suppliers } from './Suppliers';
import { StockReceipts } from './StockReceipts';

type PageKey =
  | 'dashboard'
  | 'custom-orders'
  | 'inventory'
  | 'stock-receipts'
  | 'sales'
  | 'reports'
  | 'finance'
  | ResourceKey;

function Brand() {
  return (
    <div className="brand-wordmark">
      <p className="brand-wordmark__name">Ajisai House</p>
      <p className="brand-wordmark__meta">ERP · Operasional Outlet</p>
    </div>
  );
}

function AuthScreen({ onAuthenticated }: { onAuthenticated: (session: AuthSession) => void }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const session = await apiRequest<AuthSession>(
        '/auth/login',
        { method: 'POST', body: JSON.stringify({ identifier, password }) },
        false,
      );
      setAccessToken(session.accessToken);
      onAuthenticated(session);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Login gagal. Silakan coba kembali.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-screen bg-ajisai-50 lg:grid-cols-[1.04fr_.96fr]">
      <section className="hidden overflow-hidden bg-ajisai-900 p-12 text-white lg:flex lg:flex-col xl:p-16">
        <p className="border-l-2 border-ajisai-400 pl-3 text-sm font-black uppercase tracking-[0.2em] text-white">
          Ajisai House ERP
        </p>
        <div className="my-auto max-w-xl">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-ajisai-300">
            Workspace Operasional
          </p>
          <h1 className="mt-5 text-5xl font-black leading-[1.08] tracking-tight">
            Operasional rapi, keputusan lebih pasti.
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-8 text-ajisai-200">
            Dashboard terpadu untuk memantau performa bisnis Ajisai House dengan aman.
          </p>
        </div>
        <p className="text-sm text-ajisai-300">Ajisai House · Sistem operasional internal</p>
      </section>
      <section className="flex items-center justify-center px-5 py-10 sm:px-8">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-7 shadow-card sm:p-10">
          <Brand />
          <div className="mt-10">
            <p className="section-kicker">Selamat datang kembali</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
              Masuk ke dashboard
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-500">
              Gunakan email atau username yang telah diberikan administrator.
            </p>
          </div>
          <form
            className="mt-8 space-y-5"
            onSubmit={(event) => {
              void submit(event);
            }}
          >
            <label className="block text-sm font-semibold text-slate-700">
              Email atau username
              <input
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-normal shadow-sm"
                autoComplete="username"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                disabled={busy}
                required
              />
            </label>
            <label className="block text-sm font-semibold text-slate-700">
              Kata sandi
              <div className="relative mt-2">
                <input
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 pr-20 font-normal shadow-sm"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={busy}
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-3 text-sm font-bold text-ajisai-700"
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? 'Sembunyikan' : 'Lihat'}
                </button>
              </div>
            </label>
            {error && (
              <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </p>
            )}
            <button
              className="w-full rounded-xl bg-ajisai-700 px-4 py-3 font-bold text-white shadow-lg shadow-ajisai-700/15 transition hover:bg-ajisai-800 disabled:opacity-60"
              disabled={busy}
            >
              {busy ? 'Memeriksa akun…' : 'Masuk'}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function Dashboard({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const [active, setActive] = useState<PageKey>('dashboard');
  const [outlets, setOutlets] = useState<Array<{ id: string; name: string }>>([]);
  const [activeOutletId, setActiveOutletId] = useState('');
  const [outletsLoaded, setOutletsLoaded] = useState(false);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const resources: Array<{ key: ResourceKey; label: string }> = [
    { key: 'outlets', label: 'Outlets' },
    { key: 'categories', label: 'Kategori Produk' },
    { key: 'products', label: 'Produk' },
    { key: 'suppliers', label: 'Supplier' },
    { key: 'customers', label: 'Customer' },
  ];
  const pageTitles: Record<PageKey, string> = {
    dashboard: 'Dashboard Operasional',
    outlets: 'Outlets',
    categories: 'Kategori Produk',
    products: 'Produk',
    suppliers: 'Supplier',
    customers: 'Customer',
    'custom-orders': 'Pesanan Custom',
    inventory: 'Inventory Barang Jadi',
    'stock-receipts': 'Penerimaan Barang',
    sales: 'Penjualan',
    reports: 'Laporan Keuangan',
    finance: 'Keuangan',
  };

  useEffect(() => {
    apiRequest<{ data: Array<{ id: string; name: string }> }>('/outlets?limit=100')
      .then((result) => {
        setOutlets(result.data);
        const first = user.allOutlets ? 'ALL' : (result.data[0]?.id ?? '');
        setActiveOutletId(first);
        setOutletContext(first && first !== 'ALL' ? first : null);
      })
      .catch(() => setOutlets([]))
      .finally(() => setOutletsLoaded(true));
    return () => setOutletContext(null);
  }, []);

  useEffect(() => {
    if (active === 'dashboard' || active === 'finance' || activeOutletId !== 'ALL' || !outlets[0])
      return;
    setActiveOutletId(outlets[0].id);
    setOutletContext(outlets[0].id);
  }, [active, activeOutletId, outlets]);

  function selectPage(page: PageKey) {
    if (page !== 'dashboard' && page !== 'finance' && activeOutletId === 'ALL' && outlets[0]) {
      setActiveOutletId(outlets[0].id);
      setOutletContext(outlets[0].id);
    }
    setActive(page);
  }

  const outletContextReady = Boolean(activeOutletId && activeOutletId !== 'ALL');

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-72 flex-col border-r border-slate-200 bg-white/95 p-5 lg:flex">
        <Brand />
        <nav className="mt-9 min-h-0 flex-1 overflow-y-auto pb-4 pr-1">
          <p className="px-3 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
            Menu utama
          </p>
          <button
            className={`nav-item mt-3 ${
              active === 'dashboard' ? 'nav-item-active' : 'nav-item-idle'
            }`}
            onClick={() => selectPage('dashboard')}
          >
            Dashboard
          </button>
          <p className="mt-7 px-3 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
            Master Data
          </p>
          {resources.map((resource) => (
            <button
              key={resource.key}
              className={`nav-item ${
                active === resource.key ? 'nav-item-active' : 'nav-item-idle'
              }`}
              onClick={() => selectPage(resource.key)}
            >
              {resource.label}
            </button>
          ))}
          <p className="mt-7 px-3 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400">
            Operasional
          </p>
          {user.permissions.includes('orders.read') && (
            <button
              className={`nav-item ${
                active === 'custom-orders' ? 'nav-item-active' : 'nav-item-idle'
              }`}
              onClick={() => selectPage('custom-orders')}
            >
              Pesanan Custom
            </button>
          )}
          <button
            className={`nav-item ${
              active === 'inventory' ? 'nav-item-active' : 'nav-item-idle'
            }`}
            onClick={() => selectPage('inventory')}
          >
            Inventory Barang Jadi
          </button>
          {user.permissions.includes('stock_receipts.read') && (
            <button
              className={`nav-item ${active === 'stock-receipts' ? 'nav-item-active' : 'nav-item-idle'}`}
              onClick={() => selectPage('stock-receipts')}
            >
              Penerimaan Barang
            </button>
          )}
          {user.permissions.includes('sales.read') && (
            <button
              className={`nav-item ${
                active === 'sales' ? 'nav-item-active' : 'nav-item-idle'
              }`}
              onClick={() => selectPage('sales')}
            >
              Penjualan
            </button>
          )}
          {user.permissions.includes('reports.read') && (
            <button
              className={`nav-item ${
                active === 'reports' ? 'nav-item-active' : 'nav-item-idle'
              }`}
              onClick={() => selectPage('reports')}
            >
              Laporan Keuangan
            </button>
          )}
          {user.permissions.includes('finance.read') && (
            <button
              className={`nav-item ${
                active === 'finance' ? 'nav-item-active' : 'nav-item-idle'
              }`}
              onClick={() => selectPage('finance')}
            >
              Keuangan
            </button>
          )}
        </nav>
        <div className="mt-4 shrink-0 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="truncate font-bold text-slate-900">{user.name}</p>
          <p className="mt-1 truncate text-xs text-slate-500">{user.email}</p>
          <p className="mt-3 inline-flex rounded-full bg-ajisai-100 px-2.5 py-1 text-xs font-bold text-ajisai-800">
            {user.roles[0]?.replaceAll('_', ' ') ?? 'USER'}
          </p>
        </div>
      </aside>
      <div className="lg:pl-72">
        <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 px-5 py-3.5 backdrop-blur-xl sm:px-8">
          <div className="mx-auto flex max-w-7xl items-center justify-between">
            <div className="lg:hidden">
              <Brand />
            </div>
            <div className="hidden lg:block">
              <p className="text-[11px] font-black uppercase tracking-[0.13em] text-ajisai-700">
                Ajisai House ERP
              </p>
              <p className="mt-0.5 font-bold text-slate-900">{pageTitles[active]}</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                aria-label="Outlet aktif"
                className="hidden max-w-56 rounded-xl border border-slate-300 px-3 py-2 text-sm font-bold sm:block"
                value={activeOutletId}
                onChange={(event) => {
                  setActiveOutletId(event.target.value);
                  setOutletContext(
                    event.target.value && event.target.value !== 'ALL' ? event.target.value : null,
                  );
                }}
              >
                <option value="">Pilih outlet</option>
                {user.allOutlets && <option value="ALL">Semua Outlet</option>}
                {outlets.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </option>
                ))}
              </select>
              <button
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  onLogout();
                }}
              >
                Keluar
              </button>
            </div>
          </div>
          <select
            aria-label="Pilih halaman"
            className="mx-auto mt-4 w-full max-w-7xl rounded-xl border border-slate-300 px-3 py-2 text-sm font-bold lg:hidden"
            value={active}
            onChange={(event) => selectPage(event.target.value as PageKey)}
          >
            <option value="dashboard">Dashboard</option>
            {resources.map((resource) => (
              <option key={resource.key} value={resource.key}>
                {resource.label}
              </option>
            ))}
            {user.permissions.includes('orders.read') && (
              <option value="custom-orders">Pesanan Custom</option>
            )}
            <option value="inventory">Inventory Barang Jadi</option>
            {user.permissions.includes('stock_receipts.read') && (
              <option value="stock-receipts">Penerimaan Barang</option>
            )}
            {user.permissions.includes('sales.read') && <option value="sales">Penjualan</option>}
            {user.permissions.includes('reports.read') && (
              <option value="reports">Laporan Keuangan</option>
            )}
            {user.permissions.includes('finance.read') && <option value="finance">Keuangan</option>}
          </select>
          <select
            aria-label="Outlet aktif mobile"
            className="mx-auto mt-2 w-full max-w-7xl rounded-xl border border-slate-300 px-3 py-2 text-sm font-bold sm:hidden"
            value={activeOutletId}
            onChange={(event) => {
              setActiveOutletId(event.target.value);
              setOutletContext(
                event.target.value && event.target.value !== 'ALL' ? event.target.value : null,
              );
            }}
          >
            <option value="">Pilih outlet</option>
            {user.allOutlets && <option value="ALL">Semua Outlet</option>}
            {outlets.map((outlet) => (
              <option key={outlet.id} value={outlet.id}>
                {outlet.name}
              </option>
            ))}
          </select>
        </header>
        <main id="dashboard" className="mx-auto max-w-7xl px-5 py-7 sm:px-8 sm:py-9">
          {outletsLoaded && outlets.length === 0 ? (
            <section className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
              <p className="text-4xl">!</p>
              <h1 className="mt-4 text-2xl font-black text-amber-950">
                {user.allOutlets ? 'Belum ada outlet aktif' : 'Akses outlet belum dikonfigurasi'}
              </h1>
              <p className="mt-3 text-sm leading-6 text-amber-800">
                {user.allOutlets
                  ? 'Buat atau aktifkan minimal satu outlet sebelum menggunakan dashboard.'
                  : 'Akun Anda belum memiliki assignment outlet aktif. Hubungi Super Admin untuk menghubungkan akun ke outlet sebelum melanjutkan.'}
              </p>
            </section>
          ) : active === 'inventory' ? (
            <Inventory
              key={activeOutletId}
              canAdjust={user.permissions.includes('inventory.adjust')}
              canReceive={user.permissions.includes('stock_receipts.read')}
              outletReady={outletContextReady}
              onOpenReceipts={() => setActive('stock-receipts')}
            />
          ) : active === 'stock-receipts' ? (
            <StockReceipts
              key={activeOutletId}
              outletReady={outletContextReady}
              permissions={user.permissions}
            />
          ) : active === 'custom-orders' ? (
            <CustomOrders
              key={activeOutletId}
              canWrite={user.permissions.includes('orders.write')}
              outletReady={outletContextReady}
              onCompleted={() => {
                setDashboardRefreshKey((current) => current + 1);
              }}
            />
          ) : active === 'sales' ? (
            <Sales
              key={activeOutletId}
              canWrite={user.permissions.includes('sales.write')}
              canSelectCustomer={user.permissions.includes('customers.read')}
              outletReady={outletContextReady}
              onSaleCompleted={() => {
                setDashboardRefreshKey((current) => current + 1);
              }}
            />
          ) : active === 'reports' ? (
            <FinancialReports key={activeOutletId} outletReady={outletContextReady} />
          ) : active === 'finance' ? (
            <Finance
              key={activeOutletId}
              consolidated={activeOutletId === 'ALL'}
              outletReady={Boolean(activeOutletId)}
              permissions={user.permissions}
            />
          ) : active === 'customers' ? (
            <Customers
              key={activeOutletId}
              canWrite={user.permissions.includes('customers.write')}
              outletReady={outletContextReady}
            />
          ) : active === 'suppliers' ? (
            <Suppliers
              key={activeOutletId}
              canWrite={user.permissions.includes('suppliers.write')}
              outletReady={outletContextReady}
            />
          ) : active === 'products' ? (
            <Products
              key={activeOutletId}
              canWrite={user.permissions.includes('products.write')}
              outletReady={outletContextReady}
            />
          ) : active !== 'dashboard' ? (
            <MasterData resource={active} canWrite={user.permissions.includes(`${active}.write`)} />
          ) : (
            <OperationalDashboard
              consolidated={activeOutletId === 'ALL'}
              outletReady={Boolean(activeOutletId)}
              refreshKey={dashboardRefreshKey}
              userName={user.name}
            />
          )}
        </main>
      </div>
    </div>
  );
}

export function App() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    restoreSession()
      .then((session) => {
        setUser(session.user);
        redirectAuthenticatedUser(session.user);
      })
      .catch(() => {
        clearClientSession();
        window.history.replaceState({}, '', '/login');
      })
      .finally(() => setLoading(false));
    const expire = () => {
      clearClientSession();
      setUser(null);
      window.history.replaceState({}, '', '/login');
    };
    window.addEventListener('ajisai:session-expired', expire);
    return () => window.removeEventListener('ajisai:session-expired', expire);
  }, []);

  async function logout() {
    await apiRequest('/auth/logout', { method: 'POST' }).catch(() => undefined);
    clearClientSession();
    setUser(null);
    window.history.replaceState({}, '', '/login');
  }

  if (loading) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50">
        <div className="text-center">
          <span
            aria-hidden="true"
            className="mx-auto block size-10 animate-spin rounded-full border-2 border-ajisai-200 border-t-ajisai-700"
          />
          <p className="mt-4 font-bold text-slate-600">Menyiapkan workspace…</p>
        </div>
      </main>
    );
  }

  return user ? (
    <Dashboard
      user={user}
      onLogout={() => {
        void logout();
      }}
    />
  ) : (
    <AuthScreen
      onAuthenticated={(session) => {
        setUser(session.user);
        redirectAuthenticatedUser(session.user);
      }}
    />
  );
}

function redirectAuthenticatedUser(user: AuthUser) {
  const route = user.roles.includes('OWNER')
    ? '/executive/dashboard'
    : user.roles.includes('SUPER_ADMIN')
      ? '/dashboard'
      : '/outlet/dashboard';
  window.history.replaceState({}, '', route);
}
